import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Chip,
  LinearProgress,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  Paper,
} from '@mui/material';
import {
  Search as SearchIcon,
  FolderOpen,
  CalendarToday,
  People,
  TrendingDown,
  CheckCircle,
  Schedule,
  Warning,
  Error as ErrorIcon,
  HelpOutline,
  Euro,
  Assignment,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/api';

interface ProgettoResume {
  id: number;
  titolo: string;
  cliente: string | null;
  cliente_finale: string | null;
  pm: string | null;
  data_inizio: string | null;
  data_fine: string | null;
  budget_gg_uu: number | null;
  budget_residuo_gg_uu: number | null;
  budget_pianificato_gg_uu: number | null;
  tipologia: string | null;
  id_hrp: number | null;
  num_risorse: number;
  giorni_pianificati_periodo: number;
  rendicontato_periodo: number;
  perc_budget_usato: number;
  perc_timeline: number;
  stato: 'in_corso' | 'attenzione' | 'critico' | 'completato' | 'futuro' | 'sconosciuto';
  totale_allocato: number;
  totale_rendicontato: number;
}

const STATO_CONFIG: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'info'; icon: React.ReactElement }> = {
  in_corso: { label: 'In corso', color: 'success', icon: <CheckCircle fontSize="small" /> },
  attenzione: { label: 'Attenzione', color: 'warning', icon: <Warning fontSize="small" /> },
  critico: { label: 'Critico', color: 'error', icon: <ErrorIcon fontSize="small" /> },
  completato: { label: 'Completato', color: 'default', icon: <CheckCircle fontSize="small" /> },
  futuro: { label: 'Futuro', color: 'info', icon: <Schedule fontSize="small" /> },
  sconosciuto: { label: 'N/D', color: 'default', icon: <HelpOutline fontSize="small" /> },
};

const BUDGET_COLORS: Record<string, string> = {
  in_corso: '#2e7d32',
  attenzione: '#e65100',
  critico: '#c62828',
  completato: '#757575',
  futuro: '#1565c0',
  sconosciuto: '#757575',
};

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatGG(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${Number(v).toFixed(1)} gg`;
}

const PMDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const currentYear = new Date().getFullYear();

  const [pmIdentifier, setPmIdentifier] = useState<string>(() =>
    localStorage.getItem('pm_identifier') || (user?.username ?? '')
  );
  const [pmInput, setPmInput] = useState<string>(pmIdentifier);
  const [dataInizio, setDataInizio] = useState<string>(`${currentYear}-01-01`);
  const [dataFine, setDataFine] = useState<string>(`${currentYear}-12-31`);
  const [progetti, setProgetti] = useState<ProgettoResume[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const caricaProgetti = useCallback(async (pm: string, di: string, df: string) => {
    if (!pm.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await apiClient.get(`/pm-dashboard/progetti?pm=${encodeURIComponent(pm.trim())}&dataInizio=${di}&dataFine=${df}`);
      setProgetti(resp.data);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Errore nel caricamento dei progetti');
      setProgetti([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (pmIdentifier) caricaProgetti(pmIdentifier, dataInizio, dataFine);
  }, [pmIdentifier, dataInizio, dataFine, caricaProgetti]);

  const handleSearch = () => {
    const trimmed = pmInput.trim();
    if (!trimmed) return;
    localStorage.setItem('pm_identifier', trimmed);
    setPmIdentifier(trimmed);
  };

  // KPI aggregati per il periodo
  const totalePianificatoPeriodo = progetti.reduce((s, p) => s + (p.giorni_pianificati_periodo || 0), 0);
  const totaleRendicontatoPeriodo = progetti.reduce((s, p) => s + (p.rendicontato_periodo || 0), 0);
  const progettiAttivi = progetti.filter(p => p.stato === 'in_corso' || p.stato === 'attenzione' || p.stato === 'critico').length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Dashboard PM
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Monitoraggio progetti di cui sei Project Manager
        </Typography>
      </Box>

      {/* Barra ricerca PM + periodo */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Assignment color="primary" />
        <Typography variant="body2" fontWeight="bold" sx={{ mr: 1 }}>
          Identificativo PM:
        </Typography>
        <TextField
          size="small"
          placeholder="es. mario.rossi@azienda.it"
          value={pmInput}
          onChange={e => setPmInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          sx={{ minWidth: 280 }}
        />
        <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} size="small">
          Cerca
        </Button>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Da:</Typography>
        <TextField
          size="small"
          type="date"
          value={dataInizio}
          onChange={e => setDataInizio(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 150 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>A:</Typography>
        <TextField
          size="small"
          type="date"
          value={dataFine}
          onChange={e => setDataFine(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 150 }}
        />
        {pmIdentifier && (
          <Chip
            label={`PM: ${pmIdentifier}`}
            color="primary"
            variant="outlined"
            size="small"
            onDelete={() => { setPmIdentifier(''); setPmInput(''); setProgetti([]); localStorage.removeItem('pm_identifier'); }}
          />
        )}
      </Paper>

      {/* KPI Summary */}
      {progetti.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Progetti totali', value: progetti.length, icon: <FolderOpen />, color: '#1976d2' },
            { label: 'Progetti attivi', value: progettiAttivi, icon: <CheckCircle />, color: '#2e7d32' },
            { label: 'Pianificato periodo', value: `${totalePianificatoPeriodo.toFixed(0)} gg`, icon: <Euro />, color: '#7b1fa2' },
            { label: 'Rendicontato periodo', value: `${totaleRendicontatoPeriodo.toFixed(0)} gg`, icon: <TrendingDown />, color: '#e65100' },
          ].map(kpi => (
            <Grid item xs={6} sm={3} key={kpi.label}>
              <Card sx={{ borderLeft: `4px solid ${kpi.color}` }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Box sx={{ color: kpi.color, display: 'flex' }}>{kpi.icon}</Box>
                    <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
                  </Box>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: kpi.color }}>
                    {kpi.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Stato */}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!loading && !error && pmIdentifier && progetti.length === 0 && (
        <Alert severity="info">Nessun progetto trovato per l'identificativo <strong>{pmIdentifier}</strong>. Verifica che corrisponda al campo PM inserito nei progetti.</Alert>
      )}

      {/* Griglia progetti */}
      {!loading && progetti.length > 0 && (
        <Grid container spacing={2}>
          {progetti.map(p => {
            const cfg = STATO_CONFIG[p.stato] || STATO_CONFIG.sconosciuto;
            const budgetColor = BUDGET_COLORS[p.stato] || '#757575';
            const budgetTot = p.budget_gg_uu || 0;
            const pianificatoPeriodo = p.giorni_pianificati_periodo;
            const percPianificato = budgetTot > 0 ? Math.min(100, Math.round((pianificatoPeriodo / budgetTot) * 100)) : 0;

            return (
              <Grid item xs={12} sm={6} lg={4} key={p.id}>
                <Card
                  sx={{
                    height: '100%',
                    borderTop: `3px solid`,
                    borderTopColor: `${cfg.color}.main`,
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 4 },
                  }}
                >
                  <CardActionArea
                    sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', p: 0 }}
                    onClick={() => navigate(`/pm-dashboard/progetti/${p.id}?dataInizio=${dataInizio}&dataFine=${dataFine}`)}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Stato + HRP */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Chip
                          icon={cfg.icon}
                          label={cfg.label}
                          color={cfg.color}
                          size="small"
                        />
                        {p.id_hrp && (
                          <Typography variant="caption" color="text.disabled">#{p.id_hrp}</Typography>
                        )}
                      </Box>

                      {/* Titolo */}
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, lineHeight: 1.3, wordBreak: 'break-word' }}>
                        {p.titolo}
                      </Typography>

                      {/* Cliente */}
                      {(p.cliente_finale || p.cliente) && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, wordBreak: 'break-word' }}>
                          {p.cliente_finale || p.cliente}
                        </Typography>
                      )}

                      {/* Date */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                        <CalendarToday sx={{ fontSize: 14, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(p.data_inizio)} → {formatDate(p.data_fine)}
                        </Typography>
                      </Box>

                      {/* Timeline progress */}
                      {p.data_inizio && p.data_fine && p.stato !== 'futuro' && (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                            <Typography variant="caption" color="text.secondary">Timeline</Typography>
                            <Typography variant="caption" fontWeight="bold">{p.perc_timeline}%</Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={p.perc_timeline}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: 'action.hover',
                              '& .MuiLinearProgress-bar': { bgcolor: '#1976d2', borderRadius: 3 }
                            }}
                          />
                        </Box>
                      )}

                      {/* Budget periodo */}
                      <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                          <Typography variant="caption" color="text.secondary">
                            Pianificato periodo
                          </Typography>
                          <Typography variant="caption" fontWeight="bold">
                            {formatGG(pianificatoPeriodo)}
                            {budgetTot > 0 && ` (${percPianificato}%)`}
                          </Typography>
                        </Box>
                        {budgetTot > 0 ? (
                          <Tooltip title={`Pianificato periodo: ${formatGG(pianificatoPeriodo)} su budget totale ${formatGG(budgetTot)}`}>
                            <Box sx={{ position: 'relative', height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden' }}>
                              <Box sx={{
                                position: 'absolute',
                                left: 0, top: 0,
                                height: '100%',
                                width: `${percPianificato}%`,
                                bgcolor: budgetColor,
                                borderRadius: 4,
                                transition: 'width 0.3s'
                              }} />
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            Budget totale non definito
                          </Typography>
                        )}
                      </Box>

                      {/* Totali complessivi */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Tot. allocato</Typography>
                        <Typography variant="caption" fontWeight="bold">{formatGG(p.totale_allocato)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">Tot. rendicontato</Typography>
                        <Typography variant="caption" fontWeight="bold">{formatGG(p.totale_rendicontato)}</Typography>
                      </Box>

                      <Divider sx={{ my: 1 }} />

                      {/* Footer */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <People sx={{ fontSize: 16, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.secondary">
                            {p.num_risorse} {p.num_risorse === 1 ? 'risorsa' : 'risorse'} (periodo)
                          </Typography>
                        </Box>
                        {p.tipologia && (
                          <Chip label={p.tipologia} size="small" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                        )}
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default PMDashboardPage;
